import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@carrent/core'
import { usersApi } from '@carrent/api'
import { api } from '@carrent/api'
import { PhotoUpload } from '@carrent/ui'
import { StepProgress } from '@carrent/ui'
import { PageHeader } from '@carrent/ui'

// ── Шаг 1: Базовые данные ────────────────────────────────────────────────────
function Step1({ onNext }) {
  const { user } = useAuthStore()
  const [form, setForm] = useState({
    full_name:  user?.full_name  || '',
    birth_date: user?.birth_date || '',
    email:      user?.email      || '',
  })
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(false)
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))

  const submit = async () => {
    if (!form.full_name.trim()) { setErr('Введите ФИО'); return }
    if (!form.birth_date)       { setErr('Введите дату рождения'); return }
    setErr(''); setLoading(true)
    try {
      await api.patch('/users/me/step1', form)
      onNext()
    } catch (e) {
      setErr(e.response?.data?.message || 'Ошибка сохранения')
    } finally { setLoading(false) }
  }

  return (
    <div className="flex-col" style={{ gap: 'var(--gap)' }}>
      <div className="alert alert--info">
        📋 Заполните личные данные. Они нужны для верификации вашей личности.
      </div>

      <div className="field">
        <label className="field__label">ФИО <span style={{color:'var(--danger)'}}>*</span></label>
        <input className="input" placeholder="Иванов Иван Иванович"
          value={form.full_name} onChange={set('full_name')} />
      </div>

      <div className="field">
        <label className="field__label">Дата рождения <span style={{color:'var(--danger)'}}>*</span></label>
        <input className="input" type="date" value={form.birth_date} onChange={set('birth_date')}
          max={new Date(Date.now() - 18*365*24*60*60*1000).toISOString().split('T')[0]} />
        <span className="text-muted" style={{fontSize:'0.78rem',marginTop:4,display:'block'}}>
          Минимальный возраст для аренды — 18 лет
        </span>
      </div>

      <div className="field">
        <label className="field__label">Email (для уведомлений)</label>
        <input className="input" type="email" placeholder="your@email.com"
          value={form.email} onChange={set('email')} />
      </div>

      {err && <div className="alert alert--error">{err}</div>}

      <button className="btn btn--primary btn--full" onClick={submit} disabled={loading}>
        {loading ? <span className="spinner"/> : 'Продолжить →'}
      </button>
    </div>
  )
}

// ── Шаг 2: Паспортные данные ─────────────────────────────────────────────────
function Step2({ onNext, onBack }) {
  const { user } = useAuthStore()
  const [form, setForm] = useState({
    passport_series:      user?.passport_series      || '',
    passport_number:      user?.passport_number      || '',
    passport_issued_by:   user?.passport_issued_by   || '',
    passport_issued_date: user?.passport_issued_date || '',
    passport_address:     user?.passport_address     || '',
  })
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(false)
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))

  const submit = async () => {
    if (!form.passport_series.trim())        { setErr('Введите серию паспорта'); return }
    if (!form.passport_number.trim())        { setErr('Введите номер паспорта'); return }
    if (!form.passport_issued_by.trim())     { setErr('Укажите кем выдан паспорт'); return }
    if (!form.passport_issued_date.trim())   { setErr('Укажите дату выдачи паспорта'); return }
    if (!form.passport_address.trim())       { setErr('Укажите адрес регистрации'); return }
    setErr(''); setLoading(true)
    try {
      await usersApi.step2(form)
      onNext()
    } catch (e) {
      setErr(e.response?.data?.message || 'Ошибка')
    } finally { setLoading(false) }
  }

  const req = <span style={{ color: 'var(--danger)' }}>*</span>

  return (
    <div className="flex-col" style={{ gap: 'var(--gap)' }}>
      <div className="alert alert--info">
        🪪 Введите данные паспорта РФ. Они будут проверены администратором.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gap-sm)' }}>
        <div className="field">
          <label className="field__label">Серия {req}</label>
          <input className="input" placeholder="1234" maxLength={4}
            value={form.passport_series} onChange={set('passport_series')} />
        </div>
        <div className="field">
          <label className="field__label">Номер {req}</label>
          <input className="input" placeholder="567890" maxLength={6}
            value={form.passport_number} onChange={set('passport_number')} />
        </div>
      </div>

      <div className="field">
        <label className="field__label">Кем выдан {req}</label>
        <input className="input" placeholder="ОУФМС России по г. Москве в р-не..."
          value={form.passport_issued_by} onChange={set('passport_issued_by')} />
      </div>

      <div className="field">
        <label className="field__label">Дата выдачи {req}</label>
        <input className="input" type="date"
          value={form.passport_issued_date} onChange={set('passport_issued_date')} />
      </div>

      <div className="field">
        <label className="field__label">Адрес регистрации {req}</label>
        <textarea className="input" rows={2} placeholder="г. Москва, ул. Примерная, д. 1, кв. 1"
          value={form.passport_address} onChange={set('passport_address')}
          style={{ resize: 'vertical' }} />
      </div>

      {err && <div className="alert alert--error">{err}</div>}

      <div style={{ display: 'flex', gap: 'var(--gap-sm)' }}>
        <button className="btn btn--outline" style={{flex:1}} onClick={onBack}>← Назад</button>
        <button className="btn btn--primary" style={{flex:2}} onClick={submit} disabled={loading}>
          {loading ? <span className="spinner"/> : 'Продолжить →'}
        </button>
      </div>
    </div>
  )
}

// ── Шаг 3: Фотографии ────────────────────────────────────────────────────────
const PHOTO_CONFIG = [
  {
    key:   'photo_passport_main',
    label: 'Паспорт — разворот с фото',
    hint:  'Главная страница с вашей фотографией',
    icon:  '🪪',
    required: true,
  },
  {
    key:   'photo_passport_address',
    label: 'Паспорт — прописка',
    hint:  'Страница с адресом регистрации',
    icon:  '📄',
    required: false,
  },
  {
    key:   'photo_selfie',
    label: 'Селфи',
    hint:  'Чёткое фото лица, хорошее освещение',
    icon:  '🤳',
    required: true,
  },
  {
    key:   'photo_selfie_passport',
    label: 'Селфи с паспортом',
    hint:  'Вы держите открытый паспорт рядом с лицом',
    icon:  '📸',
    required: true,
  },
]

function Step3({ onNext, onBack }) {
  const [photos, setPhotos]   = useState({})
  const [err, setErr]         = useState('')
  const [loading, setLoading] = useState(false)
  const setPhoto = (name, file) => setPhotos(p => ({ ...p, [name]: file }))

  const requiredKeys  = PHOTO_CONFIG.filter(p => p.required).map(p => p.key)
  const requiredDone  = requiredKeys.every(k => photos[k])
  const totalDone     = Object.keys(photos).length

  const submit = async () => {
    if (!requiredDone) {
      setErr('Загрузите все обязательные фото (отмечены *)'); return
    }
    setErr(''); setLoading(true)
    try {
      const fd = new FormData()
      Object.entries(photos).forEach(([k, v]) => fd.append(k, v))
      await usersApi.uploadDocuments(fd)
      onNext()
    } catch (e) {
      setErr(e.response?.data?.message || 'Ошибка загрузки фото')
    } finally { setLoading(false) }
  }

  return (
    <div className="flex-col" style={{ gap: 'var(--gap)' }}>
      <div className="alert alert--info" style={{background:'rgba(245,158,11,0.08)', borderColor:'rgba(245,158,11,0.4)', color:'var(--warning)'}}>
        📷 Нужны чёткие фото при хорошем освещении. Обязательные отмечены <span style={{color:'var(--danger)'}}>*</span>
      </div>

      {PHOTO_CONFIG.map(({ key, label, hint, icon, required }) => (
        <div key={key}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <span style={{ fontSize: '1.1rem' }}>{icon}</span>
            <span className="field__label" style={{ margin: 0 }}>
              {label} {required && <span style={{ color: 'var(--danger)' }}>*</span>}
            </span>
            {photos[key] && (
              <span style={{ marginLeft: 'auto', color: 'var(--success)', fontSize: '0.82rem' }}>✓ Загружено</span>
            )}
          </div>
          <PhotoUpload name={key} label="" hint={hint} onChange={setPhoto} />
        </div>
      ))}

      {/* Прогресс фото */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gap-sm)', padding: '8px 12px',
        background: 'var(--bg-card)', borderRadius: 'var(--r-sm)', border: '1px solid var(--border)' }}>
        <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
          Загружено: {totalDone} из {PHOTO_CONFIG.length}
        </span>
        <div style={{ flex: 1, height: 4, background: 'var(--border)', borderRadius: 99, overflow: 'hidden' }}>
          <div style={{ height: '100%', background: requiredDone ? 'var(--success)' : 'var(--accent)',
            width: `${(totalDone / PHOTO_CONFIG.length) * 100}%`, transition: 'width 0.3s' }} />
        </div>
      </div>

      {err && <div className="alert alert--error">{err}</div>}

      <div style={{ display: 'flex', gap: 'var(--gap-sm)' }}>
        <button className="btn btn--outline" style={{flex:1}} onClick={onBack}>← Назад</button>
        <button className="btn btn--primary" style={{flex:2}}
          onClick={submit} disabled={loading || !requiredDone}>
          {loading ? <span className="spinner"/> : 'Отправить заявку ✓'}
        </button>
      </div>
    </div>
  )
}

// ── Шаг 4: Подтверждение ─────────────────────────────────────────────────────
function Step4() {
  const navigate = useNavigate()
  return (
    <div style={{ textAlign: 'center', paddingTop: '1.5rem' }}>
      <div style={{ fontSize: '4rem', marginBottom: '1rem', animation: 'pulse 1s ease' }}>✅</div>
      <h2 style={{ marginBottom: 'var(--gap-sm)' }}>Заявка отправлена!</h2>
      <p className="text-muted" style={{ lineHeight: 1.8 }}>
        Ваши документы переданы администратору.<br />
        Обычно проверка занимает до 24 часов.
      </p>

      <div className="card" style={{ marginTop: 'var(--gap-lg)', textAlign: 'left', gap: 8, display: 'flex', flexDirection: 'column' }}>
        <p style={{ fontWeight: 600, marginBottom: 4 }}>📋 Что дальше:</p>
        {[
          'Администратор проверит ваши документы',
          'Вы получите уведомление об одобрении',
          'После одобрения можно бронировать машины',
        ].map((text, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <span style={{ color: 'var(--accent)', fontWeight: 700, flexShrink: 0 }}>{i + 1}.</span>
            <span className="text-muted" style={{ fontSize: '0.88rem' }}>{text}</span>
          </div>
        ))}
      </div>

      <button className="btn btn--secondary btn--full" style={{ marginTop: 'var(--gap-lg)' }}
        onClick={() => navigate('/')}>
        На главную
      </button>
    </div>
  )
}

// ── Главный компонент ─────────────────────────────────────────────────────────
const STEPS = [
  { title: 'Личные данные',    label: 'Личные' },
  { title: 'Паспорт',          label: 'Паспорт' },
  { title: 'Фотографии',       label: 'Фото' },
  { title: 'Заявка отправлена', label: 'Готово' },
]

export default function VerificationWizard() {
  const { refreshUser } = useAuthStore()
  const [step, setStep] = useState(1)
  const navigate = useNavigate()

  const next = () => {
    if (step === 3) {
      // После загрузки фото — сабмит
      submitAndAdvance()
    } else {
      setStep(s => s + 1)
    }
  }
  const back = () => setStep(s => s - 1)

  const submitAndAdvance = async () => {
    try {
      await usersApi.submit()
      // Обновляем стор — теперь status === 'pending'
      await refreshUser()
      setStep(4)
    } catch (e) {
      // submit может упасть если уже pending — просто переходим на шаг 4
      if (e.response?.status === 400) {
        await refreshUser()
        setStep(4)
      }
    }
  }

  return (
    <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <PageHeader
        title={STEPS[step - 1].title}
        onBack={step > 1 && step < 4 ? back : undefined}
        right={
          <span className="text-muted" style={{ fontSize: '0.82rem' }}>
            {step < 4 ? `${step} / 3` : ''}
          </span>
        }
      />

      <div className="p-page flex-col" style={{ gap: 'var(--gap)', flex: 1 }}>
        {/* Прогресс-бар */}
        {step < 4 && (
          <div>
            <StepProgress current={step} total={3} label={STEPS[step - 1].label} />
            {/* Индикаторы шагов */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
              {STEPS.slice(0, 3).map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div style={{
                    width: 20, height: 20, borderRadius: '50%', fontSize: '0.7rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, flexShrink: 0,
                    background: i + 1 < step ? 'var(--success)' : i + 1 === step ? 'var(--accent)' : 'var(--border)',
                    color: i + 1 <= step ? '#fff' : 'var(--text-muted)',
                  }}>
                    {i + 1 < step ? '✓' : i + 1}
                  </div>
                  <span style={{
                    fontSize: '0.72rem',
                    color: i + 1 === step ? 'var(--text)' : 'var(--text-muted)',
                    fontWeight: i + 1 === step ? 600 : 400,
                  }}>{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 1 && <Step1 onNext={() => setStep(2)} />}
        {step === 2 && <Step2 onNext={() => setStep(3)} onBack={back} />}
        {step === 3 && <Step3 onNext={submitAndAdvance} onBack={back} />}
        {step === 4 && <Step4 />}
      </div>
    </div>
  )
}
